import React, { useState } from 'react';
import { CashFlowReport } from '../../types/admin.types';
import './CashFlowCharts.css';

interface Props {
  cashFlowToday?: CashFlowReport;
  cashFlowWeek?: CashFlowReport;
  cashFlowMonth?: CashFlowReport;
}

const CashFlowCharts: React.FC<Props> = ({ cashFlowToday, cashFlowWeek, cashFlowMonth }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('month');

  const getCurrentReport = (): CashFlowReport | undefined => {
    switch (selectedPeriod) {
      case 'today':
        return cashFlowToday;
      case 'week':
        return cashFlowWeek;
      case 'month':
        return cashFlowMonth;
      default:
        return cashFlowMonth;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
  };

  const report = getCurrentReport();

  if (!report) {
    return (
      <div className="cash-flow-container">
        <p>No hay datos de flujo de caja disponibles</p>
      </div>
    );
  }

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'today':
        return 'Hoy';
      case 'week':
        return 'Esta Semana';
      case 'month':
        return 'Este Mes';
    }
  };

  // Calcular porcentaje de éxito
  const successRate = report.TotalTransactions > 0
    ? (report.CompletedTransactions / report.TotalTransactions * 100).toFixed(1)
    : '0';

  const failureRate = report.TotalTransactions > 0
    ? (report.FailedTransactions / report.TotalTransactions * 100).toFixed(1)
    : '0';

  return (
    <div className="cash-flow-container">
      {/* Header con selector de período */}
      <div className="cash-flow-header">
        <h2>💰 Reporte de Flujo de Caja - {getPeriodLabel()}</h2>
        <div className="period-selector">
          <button
            className={`period-btn ${selectedPeriod === 'today' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('today')}
          >
            Hoy
          </button>
          <button
            className={`period-btn ${selectedPeriod === 'week' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('week')}
          >
            Semana
          </button>
          <button
            className={`period-btn ${selectedPeriod === 'month' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('month')}
          >
            Mes
          </button>
        </div>
      </div>

      {/* Resumen de Totales */}
      <div className="totals-summary">
        <div className="summary-card total">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <div className="summary-value">{report.TotalTransactions}</div>
            <div className="summary-label">Total Transacciones</div>
          </div>
        </div>

        <div className="summary-card success">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <div className="summary-value">{report.CompletedTransactions}</div>
            <div className="summary-label">Completadas ({successRate}%)</div>
            <div className="summary-amount">{formatCurrency(report.CompletedAmount)}</div>
          </div>
        </div>

        <div className="summary-card error">
          <div className="summary-icon">❌</div>
          <div className="summary-content">
            <div className="summary-value">{report.FailedTransactions}</div>
            <div className="summary-label">Fallidas ({failureRate}%)</div>
            <div className="summary-amount">{formatCurrency(report.FailedAmount)}</div>
          </div>
        </div>

        <div className="summary-card warning">
          <div className="summary-icon">⏳</div>
          <div className="summary-content">
            <div className="summary-value">{report.PendingTransactions}</div>
            <div className="summary-label">Pendientes</div>
            <div className="summary-amount">{formatCurrency(report.PendingAmount)}</div>
          </div>
        </div>
      </div>

      {/* Gráfico de Barras - Desglose Diario */}
      {report.DailyBreakdown && report.DailyBreakdown.length > 0 && (
        <div className="chart-section">
          <h3>📈 Desglose Diario</h3>
          <div className="bar-chart">
            {report.DailyBreakdown.map((day, index) => {
              const maxAmount = Math.max(...report.DailyBreakdown.map(d => d.TotalAmount));
              const heightPercentage = maxAmount > 0 ? (day.TotalAmount / maxAmount * 100) : 0;

              return (
                <div key={index} className="bar-group">
                  <div className="bar-container">
                    <div
                      className="bar"
                      style={{ height: `${heightPercentage}%` }}
                      title={`${formatCurrency(day.TotalAmount)} - ${day.TransactionCount} transacciones`}
                    >
                      <div className="bar-value">
                        {day.TransactionCount > 0 && formatCurrency(day.TotalAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="bar-label">
                    <div className="bar-date">{formatDate(day.Date)}</div>
                    <div className="bar-count">{day.TransactionCount}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Desglose por Método de Pago */}
      {report.PaymentMethodBreakdown && report.PaymentMethodBreakdown.length > 0 && (
        <div className="payment-methods-section">
          <h3>💳 Desglose por Método de Pago</h3>
          <div className="payment-methods-table">
            <table>
              <thead>
                <tr>
                  <th>Método de Pago</th>
                  <th>Marca</th>
                  <th>Transacciones</th>
                  <th>Monto Total</th>
                  <th>% del Total</th>
                </tr>
              </thead>
              <tbody>
                {report.PaymentMethodBreakdown.map((method, index) => {
                  const percentage = report.CompletedAmount > 0
                    ? (method.TotalAmount / report.CompletedAmount * 100).toFixed(1)
                    : '0';

                  return (
                    <tr key={index}>
                      <td><strong>{method.PaymentMethod || 'Desconocido'}</strong></td>
                      <td>{method.PaymentBrand || '—'}</td>
                      <td>{method.TransactionCount}</td>
                      <td className="amount">{formatCurrency(method.TotalAmount)}</td>
                      <td>
                        <div className="percentage-bar">
                          <div
                            className="percentage-fill"
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="percentage-value">{percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={2}><strong>TOTAL</strong></td>
                  <td><strong>{report.CompletedTransactions}</strong></td>
                  <td className="amount"><strong>{formatCurrency(report.CompletedAmount)}</strong></td>
                  <td><strong>100%</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Información del Período */}
      <div className="period-info">
        <p>
          📅 Período: {new Date(report.StartDate).toLocaleDateString('es-CO')} - {new Date(report.EndDate).toLocaleDateString('es-CO')}
        </p>
      </div>
    </div>
  );
};

export default CashFlowCharts;
